import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';

const DURATION_MS = 1600;
const OPACITY = .3;
const SEGMENTS = 32, SIDES = 6, RADIUS = .026;
const DASH_SPEED = 1.4; // Metres per real second, from hero to target.

interface Arrow { group: THREE.Group; shaft: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>; head: THREE.Mesh<THREE.ConeGeometry, THREE.MeshBasicMaterial>; travel: { value: number }; started: number }

/** A brief overhead cue for a hero's new intention, driven by the rendered snapshots. */
export class TargetArrows {
  get visualObjects(): THREE.Object3D[] { return [...this.arrows.values()].map(arrow => arrow.group); }
  private targets = new Map<string, string | null>();
  private arrows = new Map<string, Arrow>();
  // Faded arrows wait here for the next target change: disposing their materials would release the dash shader, which three
  // then compiles again (a stall mid-fight) for the next arrow.
  private spare: Arrow[] = [];
  private curve = new THREE.QuadraticBezierCurve3();
  private point = new THREE.Vector3();
  private previousPoint = new THREE.Vector3();
  private tangent = new THREE.Vector3();
  private side = new THREE.Vector3();
  private normal = new THREE.Vector3();
  private up = new THREE.Vector3(0, 1, 0);
  constructor(private scene: THREE.Scene, private heights: ReadonlyMap<string, number>) {}

  private remove(id: string) {
    const arrow = this.arrows.get(id);
    if (!arrow) return;
    arrow.group.removeFromParent(); this.spare.push(arrow);
    this.arrows.delete(id);
  }
  clear() {
    for (const id of this.arrows.keys()) this.remove(id);
    for (const { shaft, head } of this.spare.splice(0)) { shaft.geometry.dispose(); head.geometry.dispose(); shaft.material.dispose(); head.material.dispose(); }
    this.targets.clear();
  }

  private create(id: string, targetId: string, now: number) {
    const arrow = this.spare.pop() ?? this.build();
    arrow.group.userData = { heroId: id, targetId }; arrow.travel.value = 0; arrow.started = now;
    this.scene.add(arrow.group);
    this.arrows.set(id, arrow);
  }

  /** A target arrow for compiling its shaders before play. */
  warmup(): THREE.Object3D { return this.build().group; }
  private build(): Arrow {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array((SEGMENTS + 1) * SIDES * 3), 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('targetDistance', new THREE.BufferAttribute(new Float32Array((SEGMENTS + 1) * SIDES), 1).setUsage(THREE.DynamicDrawUsage));
    const indices: number[] = [];
    for (let i = 0; i < SEGMENTS; i++) for (let j = 0; j < SIDES; j++) {
      const a = i * SIDES + j, b = i * SIDES + (j + 1) % SIDES;
      indices.push(a, b, a + SIDES, b, b + SIDES, a + SIDES);
    }
    geometry.setIndex(indices);
    const material = new THREE.MeshBasicMaterial({ color: 0xef4545, transparent: true, opacity: OPACITY, depthWrite: false, toneMapped: false });
    const headMaterial = material.clone(), travel = { value: 0 };
    // Dash spacing stays in world metres as the curve stretches. Subtracting travel
    // moves each dash toward increasing distance: from the hero to the enemy.
    material.onBeforeCompile = shader => {
      shader.uniforms.targetTravel = travel;
      shader.vertexShader = 'attribute float targetDistance; varying float vTargetDistance;\n' + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvTargetDistance = targetDistance;');
      shader.fragmentShader = 'uniform float targetTravel; varying float vTargetDistance;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>\nif (mod(vTargetDistance - targetTravel, 0.7) > 0.42) discard;');
    };
    material.customProgramCacheKey = () => 'target-arrow-dashes';
    const shaft = new THREE.Mesh(geometry, material);
    const head = new THREE.Mesh(new THREE.ConeGeometry(.12, .3, 12), headMaterial);
    const group = new THREE.Group(); group.name = 'target-arrow';
    group.add(shaft, head);
    return { group, shaft, head, travel, started: 0 };
  }

  update(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, now = performance.now()) {
    const units = [...snapshot.heroes, ...snapshot.enemies];
    for (const id of this.targets.keys()) if (!snapshot.heroes.some(hero => hero.id === id)) { this.remove(id); this.targets.delete(id); }
    for (const hero of snapshot.heroes) {
      const targetId = hero.combat.targetId;
      const target = units.find(unit => unit.id === targetId);
      const visible = !hero.health.dead && hero.connected && target && !target.health.dead && !('hidden' in target && target.hidden) && positions.get(hero.id)?.visible !== false && positions.get(target.id)?.visible !== false;
      const changed = (this.targets.get(hero.id) ?? null) !== targetId;
      this.targets.set(hero.id, targetId);
      if (changed || !visible) this.remove(hero.id);
      if (changed && visible && targetId) this.create(hero.id, targetId, now);
      const arrow = this.arrows.get(hero.id);
      if (!arrow || !target) continue;
      const progress = (now - arrow.started) / DURATION_MS;
      if (progress >= 1) { this.remove(hero.id); continue; }
      const previous = [...earlier.heroes, ...earlier.enemies];
      for (const [unit, out] of [[hero, this.curve.v0], [target, this.curve.v2]] as const) {
        const before = previous.find(candidate => candidate.id === unit.id) ?? unit;
        const position = positions.get(unit.id)?.position;
        out.set(position?.x ?? before.x + (unit.x - before.x) * alpha, (position?.y ?? 0) + (this.heights.get(unit.id) ?? 2) + .4, position?.z ?? before.z + (unit.z - before.z) * alpha);
      }
      const span = this.curve.v0.distanceTo(this.curve.v2);
      this.curve.v1.copy(this.curve.v0).lerp(this.curve.v2, .5); this.curve.v1.y += Math.min(3, 1.3 + span * .15);
      const vertices = arrow.shaft.geometry.getAttribute('position') as THREE.BufferAttribute;
      const distances = arrow.shaft.geometry.getAttribute('targetDistance') as THREE.BufferAttribute;
      let length = 0;
      for (let i = 0; i <= SEGMENTS; i++) {
        this.curve.getPoint(i / SEGMENTS, this.point); this.curve.getTangent(i / SEGMENTS, this.tangent);
        if (i > 0) length += this.previousPoint.distanceTo(this.point);
        this.previousPoint.copy(this.point);
        this.side.set(-this.tangent.z, 0, this.tangent.x);
        if (this.side.lengthSq() < 1e-8) this.side.set(1, 0, 0);
        this.side.normalize(); this.normal.crossVectors(this.tangent, this.side).normalize();
        for (let j = 0; j < SIDES; j++) {
          const angle = j / SIDES * Math.PI * 2, a = Math.cos(angle) * RADIUS, b = Math.sin(angle) * RADIUS;
          vertices.setXYZ(i * SIDES + j, this.point.x + this.side.x * a + this.normal.x * b, this.point.y + this.side.y * a + this.normal.y * b, this.point.z + this.side.z * a + this.normal.z * b);
          distances.setX(i * SIDES + j, length);
        }
      }
      vertices.needsUpdate = distances.needsUpdate = true; arrow.shaft.geometry.boundingBox = null; arrow.shaft.geometry.computeBoundingSphere();
      arrow.travel.value = (now - arrow.started) / 1000 * DASH_SPEED;
      this.curve.getTangent(1, this.tangent);
      arrow.head.position.copy(this.curve.v2).addScaledVector(this.tangent, -.15);
      arrow.head.quaternion.setFromUnitVectors(this.up, this.tangent);
      // A quick entrance, a readable hold, then a gentle fade; wall time also works while paused.
      arrow.shaft.material.opacity = arrow.head.material.opacity = OPACITY * Math.min(1, progress * 12, (1 - progress) * 3);
    }
  }
}
